import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router"; 
import useApi from "../../../../core/customhook/useApi";
import z from "zod";
import Swal from "sweetalert2";
import { all_routes } from "../../../router/all_routes";
import CollapseHeader from "../../../../core/common/collapse-header/collapse-header";
import { Link } from "react-router-dom";
import SnipperLoader from "../../../../core/common/loader/SnipperLoader";

const required = "This field is required.";

export const updateDepartmentSchema = z.object({
  department_name: z.string().trim().min(1, required),
  department_code: z.string().trim().min(1, required),
  description: z.string().trim().min(1, required),
});

const UpdateLob = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const departmentId = location.state?.departmentId;

  const [formData, setFormData] = useState({
    department_name: "",
    department_code: "",
    description: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const getDepartmentByID = useApi<any>(
    `/api/v1/master/departments/${departmentId}`,
    {
      method: "GET",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  const updateDepartment = useApi<any>(
    `/api/v1/master/departments/${departmentId}`,
    {
      method: "PATCH",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  const getLobloading = getDepartmentByID.loading;
  const updateloading = updateDepartment.loading;

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  useEffect(() => {
    const getDepartmentDetails = async () => {
      if (!departmentId) return;

      try {
        const res = await getDepartmentByID.callApi();

        if (res?.success) {
          const data = res.data;
          setFormData(data);
        }
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Unable to load department details.",
        });
      }
    };
    getDepartmentDetails();
  }, [departmentId]);

  const handleSubmit = async () => {
    const result = updateDepartmentSchema.safeParse(formData);

    if (!result.success) {
      const validationErrors: Record<string, string> = {};

      result.error.issues.forEach((issue) => {
        validationErrors[issue.path[0] as string] = issue.message;
      });

      setErrors(validationErrors);
      return;
    }

    setErrors({});

    try {
      const res = await updateDepartment.callApi(formData);

      if (res?.success) {
        Swal.fire({
          icon: "success",
          title: "Updated",
          text: "Department updated successfully.",
          timer: 1500,
          showConfirmButton: true,
        });

        navigate(all_routes.lobList);
      }
    } catch (error: any) {
      Swal.fire({
        icon: "error",
        title: "Update Failed",
        text: error?.response?.data?.message ?? "Unable to update department.",
      });
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Breadcrumb */}
        <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
          <div className="my-auto mb-2">
            <h2 className="mb-1">Update Line of Business</h2>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={all_routes.superAdminDashboard}>
                    <i className="ti ti-smart-home" />
                  </Link>
                </li>
                <li className="breadcrumb-item">Account Activation</li>
                <li className="breadcrumb-item">Account Configuration</li>
                <li className="breadcrumb-item active">Update LoB</li>
              </ol>
            </nav>
          </div>
          <div className="head-icons ms-2">
            <CollapseHeader />
          </div>
        </div>
        {getLobloading ? (
          <SnipperLoader />
        ) : (
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">Update Line of Business</h5>
            </div>

            <div className="card-body">
              <div className="row">
                {/* Department Code */}
                <div className="col-md-4 mb-3">
                  <label className="form-label">
                    Department Code <span className="text-danger">*</span>
                  </label>

                  <input
                    type="text"
                    name="department_code"
                    className={`form-control ${
                      errors.department_code ? "is-invalid" : ""
                    }`}
                    placeholder="Enter Department Code"
                    value={formData.department_code}
                    onChange={handleChange}
                  />

                  {errors.department_code && (
                    <div className="invalid-feedback">
                      {errors.department_code}
                    </div>
                  )}
                </div>

                {/* Department Name */}

                <div className="col-md-4 mb-3">
                  <label className="form-label">
                    Department <span className="text-danger">*</span>
                  </label>

                  <input
                    type="text"
                    name="department_name"
                    className={`form-control ${
                      errors.department_name ? "is-invalid" : ""
                    }`}
                    placeholder="Enter Department Name"
                    value={formData.department_name}
                    onChange={handleChange}
                  />

                  {errors.department_name && (
                    <div className="invalid-feedback">
                      {errors.department_name}
                    </div>
                  )}
                </div>

                {/* Description */}
                <div className="col-md-4 mb-3">
                  <label className="form-label">
                    Department Description
                    <span className="text-danger">*</span>
                  </label>

                  <input
                    type="text"
                    name="description"
                    className={`form-control ${
                      errors.description ? "is-invalid" : ""
                    }`}
                    placeholder="Enter Department Description"
                    value={formData.description}
                    onChange={handleChange}
                  />

                  {errors.description && (
                    <div className="invalid-feedback">{errors.description}</div>
                  )}
                </div>
              </div>

              <hr />

              <div className="d-flex justify-content-end gap-2">
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={() => navigate(-1)}
                >
                  Back
                </button>

                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSubmit}
                  disabled={updateloading}
                >
                  {updateloading ? "Updating..." : "Update"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UpdateLob;
